import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import classnames from "classnames";
import TextareaAutosize from "react-textarea-autosize";

import "./Parent.styles.scss";
import { Avatar, TooltipDiv } from "components";
import { useAudiencesSelector, useCommentsActions, useDealIdSelector, useUserIdSelector } from "store";
import { IAudiences, ICommentChild, ICommentParent } from "store/types";
import { AudienceIcon } from "../Audience-icon/AudienceIcon";
import { FindTargetRefType } from "../Section/Section";
import { ReactComponent as GroupIcon } from "../../../assets/icons/group.svg";
import { ReactComponent as ArrowLeftGray } from "../../../assets/icons/arrow-left-gray.svg";
import { Child } from "../Child/Child";
import { CommentInput } from "../Comment-input/CommentInput";
import { useActionHook, useCommentsUrlHooks, useFormatDateHook } from "hooks";
import { Button } from "components/forms";
import { ContextMenu } from "components/popups";

interface IParent {
  comment: ICommentParent;
  findTargetRef: FindTargetRefType;
}

export const Parent: React.FC<IParent> = ({ comment, findTargetRef }) => {
  const { created_at, text, audience, question_id, id, child_comments, form_id, user_id } = comment;
  const { deleteCommentAction, updateCommentAction } = useCommentsActions();
  const { action: updateComment, loading } = useActionHook(updateCommentAction);
  const audiences: IAudiences = useAudiencesSelector();
  const { replyId, commentId } = useCommentsUrlHooks();

  const userId: number | undefined = useUserIdSelector();
  const dealId: number | undefined = useDealIdSelector();

  const [isReplyListActive, setIsReplyListActive] = useState<boolean>(false);
  const [isReplyInput, setIsReplyInput] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const [btnStatus, setBtnStatus] = useState<boolean>(true);

  const formatDate = useFormatDateHook();
  useEffect(() => {
    if (replyId) {
      if (child_comments[replyId]) {
        setIsReplyListActive(true);
      }
    }
  }, [replyId]);

  const onEdit: (() => void) | undefined = useMemo(() => {
    setBtnStatus(false);
    if (userId === user_id) {
      return () => setIsDisabled(false);
    }
  }, [userId, user_id]);

  const onDelete: (() => Promise<void>) | undefined = useMemo(() => {
    if (dealId && userId === user_id) {
      return async () => await deleteCommentAction(dealId, id, question_id?.question_uuid);
    }
  }, [dealId, userId, user_id, question_id]);

  const editSubmit: () => void = useCallback(async () => {
    if (inputRef.current && dealId) {
      await updateComment({
        deal_id: dealId,
        comment_id: id,
        text: inputRef.current.value,
        form_id,
        audience
      });
    }
    setIsDisabled(true);
  }, [inputRef, dealId, id, form_id]);

  const editCancel: () => void = useCallback(() => {
    setIsDisabled(true);
    if (inputRef.current) {
      inputRef.current.value = text;
    }
  }, [text, inputRef]);

  const renderChild: JSX.Element[] = useMemo(() => {
    return Object.values(child_comments).map((child: ICommentChild) => {
      return <Child findTargetRef={findTargetRef} key={child.id} comment={child} />;
    });
  }, [child_comments]);

  const setTargetRef = useCallback(
    (ref: HTMLDivElement) => (commentId === id && !replyId ? findTargetRef(ref) : null),
    [commentId, replyId, id]
  );

  return (
    <div
      ref={setTargetRef}
      className={classnames("parent", "flex-col-fs-fs", {
        "parent--marked": commentId === id
      })}
    >
      <div className={"content flex-col-fs-st"}>
        <div className="author-block flex-row-sb-fc">
          <div className="author flex-row-fs-fc">
            <Avatar className="author-image" src={comment?.user?.profile_photo_url} />
            <div className="flex-col-fs-fs">
              <span className="author-name">{`${comment?.user?.first_name} ${comment?.user?.last_name}`}</span>
              <div className="flex-row-fc-fc">
                <span className="date-row">{formatDate({ date: created_at, format: "HH:mm, MMM DD, YYYY" })} |</span>
                <TooltipDiv tooltipMessage={audience ? audiences[audience] : "No audience"}>
                  <div className="recipient-icon">{audience ? <AudienceIcon type={audience} /> : <GroupIcon />}</div>
                </TooltipDiv>
              </div>
            </div>
          </div>
          <ContextMenu
            offsetX={-150}
            options={{
              onEdit,
              onDelete,
              onLink: () => console.log("Get Comment Link")
            }}
          />
        </div>
        <div className="comment-spacer-wrap">
          {isDisabled ? (
            <div
              className={classnames(
                // "comment-input__textarea",
                "comment-message__text",
                "comment-message__edit-span",
                "no-outline"
              )}
            >
              {text}
            </div>
          ) : (
            <TextareaAutosize
              ref={inputRef}
              defaultValue={text}
              disabled={isDisabled}
              className={classnames("comment-input__textarea", "comment-message__text", "no-outline")}
            />
          )}
          {!isDisabled && (
            <div className="btn-block opacity-appearance flex-row-sb-fc">
              <Button className="comment-input__button" variant="light-border" title="Cancel" onClick={editCancel} />
              <Button
                className="comment-input__button"
                isLoading={loading}
                // disabled={!inputRef.current?.value}
                disabled={btnStatus}
                title="Post"
                onClick={editSubmit}
              />
            </div>
          )}
        </div>
        {!!Object.entries(comment.child_comments).length && (
          <div onClick={() => setIsReplyListActive(!isReplyListActive)} className="reply-control flex-row-fs-fc">
            <ArrowLeftGray className="reply-arrow scale-click" stroke={isReplyListActive ? "#26A6AE" : "#597380"} />
            <span
              className={classnames("view-reply", "scale-click", {
                "view-reply--green": isReplyListActive
              })}
            >
              {isReplyListActive ? "Hide replies" : `View replies (${Object.entries(comment.child_comments).length})`}
            </span>
          </div>
        )}
        {isReplyListActive && renderChild}
      </div>
      <div className="comment-message__reply-block flex-row-fs-fc">
        {isReplyInput ? (
          <CommentInput
            onCancel={() => setIsReplyInput(false)}
            parentCommentId={id}
            question_id={question_id?.question_id}
            parentAudience={audience}
          />
        ) : (
          <span onClick={() => setIsReplyInput(true)} className="comment-message__reply-title scale-click">
            Reply
          </span>
        )}
      </div>
    </div>
  );
};
